import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';

import { personName } from '@/shared/lib/identity';
import { Avatar } from '@/shared/ui/Avatar';
import { BrandLogo } from '@/shared/ui/BrandLogo';
import { cn } from '@/shared/ui/cn';
import { CountBadge } from '@/shared/ui/CountBadge';
import { Icon } from '@/shared/ui/Icon';
import { Tooltip } from '@/shared/ui/Popover';

import { MoreMenu } from '@/app/shell/MoreMenu';
import type { NavItem } from '@/app/shell/navigation';
import { NAV, activeKey, isWorkspace } from '@/app/shell/navigation';
import { useNavBadges } from '@/app/shell/useNavBadges';
import { SPACE_ROOTS, useAppMode, useMe } from '@/stores/session.store';

/**
 * La barre latérale d'ordinateur, à la manière d'Instagram : icônes seules sur
 * tablette et petit écran (72 px, info-bulles), icônes et libellés dès 1280 px.
 * L'entrée courante prend son icône pleine et sa couleur d'actif : l'or sur la
 * place de marché, le marine dans les espaces de travail. En pied : les
 * notifications, « Mon espace » et le menu « Plus ».
 */
export function Sidebar() {
  const { t } = useTranslation(['common', 'web', 'account']);
  const mode = useAppMode();
  const me = useMe();
  const { pathname } = useLocation();
  const badges = useNavBadges();
  const active = activeKey(mode, pathname);
  const workspace = isWorkspace(mode);

  const spaceLabel =
    mode === 'seller'
      ? t('account:menu.spacePro')
      : mode === 'admin'
        ? t('account:menu.spaceAdmin')
        : mode === 'agent'
          ? t('account:menu.spaceAgent')
          : null;

  return (
    <nav
      aria-label={t('web:shell.mainNav')}
      className="fixed inset-y-0 left-0 z-30 hidden w-[72px] flex-col border-r border-line bg-surface px-3 pt-5 pb-4 md:flex xl:w-[244px]"
    >
      <Link
        to={SPACE_ROOTS[mode]}
        className="mb-5 flex h-11 items-center gap-2.5 rounded-md px-2 hover:bg-sunken xl:px-3"
        aria-label="BUL"
      >
        <BrandLogo size={30} />
        <span className="hidden min-w-0 flex-col xl:flex">
          <span className="text-title2 leading-5 tracking-[-0.04em] text-fg">BUL</span>
          {spaceLabel ? <span className="truncate text-micro font-normal text-fg-muted">{spaceLabel}</span> : null}
        </span>
      </Link>

      <div className="scrollbar-none -mx-1 flex flex-1 flex-col gap-4 overflow-y-auto px-1">
        {NAV[mode].map((section) => (
          <div key={section.key} className="flex flex-col gap-0.5">
            {section.title ? (
              <span className="hidden px-3 pt-1 pb-1.5 text-micro font-normal text-fg-faint xl:block">{t(section.title)}</span>
            ) : null}
            {section.items.map((item) => (
              <SidebarLink
                key={item.key}
                item={item}
                active={active === item.key}
                workspace={workspace}
                badge={item.badge ? badges[item.badge] : 0}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-col gap-0.5 border-t border-line-soft pt-3">
        <SidebarLink
          item={{ key: 'notifications', label: 'web:shell.notifications', icon: 'bell', iconActive: 'bell', to: '/notifications' }}
          active={pathname.startsWith('/notifications')}
          workspace={workspace}
          badge={badges.notifications}
        />
        <Tooltip label={t('account:menu.title')}>
          <NavLink
            to="/menu"
            className={({ isActive }) =>
              cn(
                'flex h-12 items-center gap-4 rounded-md px-3 transition-colors hover:bg-sunken',
                isActive && 'bg-sunken',
              )
            }
          >
            <Avatar name={me ? personName(me) : undefined} src={me?.photoUrl} size="xs" className="size-6 text-[10px]" />
            <span className="hidden truncate text-subtle text-fg xl:block">{me ? personName(me) : t('account:menu.title')}</span>
          </NavLink>
        </Tooltip>
        <MoreMenu
          trigger={
            <button
              type="button"
              className="flex h-12 items-center gap-4 rounded-md px-3 text-fg transition-colors hover:bg-sunken data-[state=open]:bg-sunken"
              aria-label={t('web:shell.more')}
            >
              <Icon name="menu" size={24} />
              <span className="hidden text-subtle xl:block">{t('web:shell.more')}</span>
            </button>
          }
        />
      </div>
    </nav>
  );
}

function SidebarLink({
  item,
  active,
  workspace,
  badge,
}: {
  item: NavItem;
  active: boolean;
  workspace: boolean;
  badge: number;
}) {
  const { t } = useTranslation(['common', 'web', 'account']);
  const label = t(item.label);
  return (
    <Tooltip label={label}>
      <Link
        to={item.to}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group flex h-12 items-center gap-4 rounded-md px-3 transition-colors hover:bg-sunken',
          active && 'font-medium',
        )}
      >
        <span className="relative grid place-items-center">
          <Icon
            name={active ? item.iconActive : item.icon}
            size={24}
            className={cn(
              'transition-transform duration-150 group-hover:scale-105',
              active ? (workspace ? 'text-primary' : 'text-accent') : 'text-fg',
            )}
          />
          {badge > 0 ? <CountBadge value={badge} max={9} className="absolute -top-1.5 -right-2" /> : null}
        </span>
        <span
          className={cn(
            'hidden truncate text-subtle xl:block',
            active ? (workspace ? 'text-primary-text' : 'text-accent-text') : 'text-fg',
          )}
        >
          {label}
        </span>
      </Link>
    </Tooltip>
  );
}
